import type { Point, Wall, WallWithTempId } from '../types';
import { distance } from './geometryUtils';
import { INITIAL_TOUCH_SELECTION_RADIUS, WALL_RESIZE_TOUCH_TOLERANCE } from '../constants/floorPlanConstants';

// Enhanced snap configuration for better endpoint detection
const BASE_SNAP_DISTANCE = 3; // pixels - base 3cm snap distance
const ENDPOINT_SNAP_MULTIPLIER = 8; // Much more generous for endpoints (24px base)
const TOUCH_ENDPOINT_MULTIPLIER = 15; // Even more generous for touch (45px base)
const ENDPOINT_PRIORITY = 1;
const INTERSECTION_PRIORITY = 2; // AutoCAD prioritizes intersections over midpoints
const MIDPOINT_PRIORITY = 3;
const PERPENDICULAR_PRIORITY = 4;
const PARALLEL_PRIORITY = 5;
const NEAREST_PRIORITY = 6;
const LINE_INTERSECTION_PRIORITY = 1.5; // Between endpoint and intersection priority

// Helper function to get scale-aware snap distances
const getSnapDistances = (scale: number = 1, isTouch: boolean = false) => {
  const baseDistance = BASE_SNAP_DISTANCE;
  const scaleAdjustedBase = Math.max(baseDistance / scale, baseDistance); // Never smaller than base
  
  return {
    general: scaleAdjustedBase,
    endpoint: isTouch 
      ? Math.max(scaleAdjustedBase * TOUCH_ENDPOINT_MULTIPLIER, WALL_RESIZE_TOUCH_TOLERANCE / 3) 
      : scaleAdjustedBase * ENDPOINT_SNAP_MULTIPLIER,
    intersection: scaleAdjustedBase * 2,
    midpoint: scaleAdjustedBase * 1.5,
    perpendicular: scaleAdjustedBase,
    nearest: scaleAdjustedBase
  };
};

export interface SimpleSnapResult {
  point: Point;
  type: 'endpoint' | 'midpoint' | 'intersection' | 'perpendicular' | 'parallel' | 'nearest' | 'none';
  wall?: WallWithTempId;
  priority: number;
}

/**
 * Simple, professional wall snapping system based on AutoCAD's proven approach
 * This replaces all the complex systems with one that actually works
 */
export class SimpleWallSnapping {
  
  /**
   * Find the best snap point with enhanced endpoint detection
   */
  static findSnapPoint(mousePoint: Point, walls: WallWithTempId[], scale: number = 1, isTouch: boolean = false): SimpleSnapResult {
    const snapCandidates: SimpleSnapResult[] = [];
    const snapDistances = getSnapDistances(scale, isTouch);
    
    // Enhanced snap detection - debug logs removed to reduce console noise
    
    // 1. Find all possible snap points with enhanced endpoint detection
    for (const wall of walls) {
      // Enhanced endpoint snaps (highest priority) - much more generous
      const startDist = distance(mousePoint, wall.start);
      if (startDist <= snapDistances.endpoint) {
        snapCandidates.push({
          point: wall.start,
          type: 'endpoint',
          wall,
          priority: ENDPOINT_PRIORITY
        });
        
        // Endpoint snap candidate (start) - debug logs removed
      }
      
      const endDist = distance(mousePoint, wall.end);
      if (endDist <= snapDistances.endpoint) {
        snapCandidates.push({
          point: wall.end,
          type: 'endpoint',
          wall,
          priority: ENDPOINT_PRIORITY
        });
        
        // Endpoint snap candidate (end) - debug logs removed
      }
      
      // Midpoint snap (only if endpoints didn't snap to avoid confusion)
      if (startDist > snapDistances.endpoint && endDist > snapDistances.endpoint) {
        const midpoint = {
          x: (wall.start.x + wall.end.x) / 2,
          y: (wall.start.y + wall.end.y) / 2
        };
        const midDist = distance(mousePoint, midpoint);
        if (midDist <= snapDistances.midpoint) {
          snapCandidates.push({
            point: midpoint,
            type: 'midpoint',
            wall,
            priority: MIDPOINT_PRIORITY
          });
        }
      }
      
      // Perpendicular snap (project mouse point onto wall) - only for precise work
      if (!isTouch) {
        const perpPoint = this.getPerpendicularPoint(mousePoint, wall);
        if (perpPoint && distance(mousePoint, perpPoint) <= snapDistances.perpendicular) {
          snapCandidates.push({
            point: perpPoint,
            type: 'perpendicular',
            wall,
            priority: PERPENDICULAR_PRIORITY
          });
        }
      }
      
      // Nearest point on wall (lowest priority) - reduced for touch to avoid conflicts
      if (!isTouch || snapCandidates.length === 0) {
        const nearestPoint = this.getNearestPointOnWall(mousePoint, wall);
        if (nearestPoint && distance(mousePoint, nearestPoint) <= snapDistances.nearest) {
          snapCandidates.push({
            point: nearestPoint,
            type: 'nearest',
            wall,
            priority: NEAREST_PRIORITY
          });
        }
      }
    }
    
    // 2. Find intersections between walls (reduced for touch to avoid confusion)
    if (!isTouch || snapCandidates.length === 0) {
      for (let i = 0; i < walls.length; i++) {
        for (let j = i + 1; j < walls.length; j++) {
          const intersection = this.getWallIntersection(walls[i], walls[j]);
          if (intersection && distance(mousePoint, intersection) <= snapDistances.intersection) {
            snapCandidates.push({
              point: intersection,
              type: 'intersection',
              priority: INTERSECTION_PRIORITY
            });
          }
        }
      }
    }
    
    // 3. Return the best snap based on priority and distance
    if (snapCandidates.length === 0) {
      // No snap candidates found - debug logs removed
      return {
        point: mousePoint,
        type: 'none',
        priority: 999
      };
    }
    
    // Sort by priority first, then by distance
    snapCandidates.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return distance(mousePoint, a.point) - distance(mousePoint, b.point);
    });
    
    const bestSnap = snapCandidates[0];
    // Best snap selection - debug logs removed
    
    return bestSnap;
  }
  
  /**
   * Get perpendicular point from mouse to wall
   */
  private static getPerpendicularPoint(point: Point, wall: Wall): Point | null {
    const wallVector = {
      x: wall.end.x - wall.start.x,
      y: wall.end.y - wall.start.y
    };
    
    const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
    if (wallLength === 0) return null;
    
    const pointVector = {
      x: point.x - wall.start.x,
      y: point.y - wall.start.y
    };
    
    const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength * wallLength);
    
    // Only return if projection is on the wall segment
    if (projection < 0 || projection > 1) return null;
    
    return {
      x: wall.start.x + projection * wallVector.x,
      y: wall.start.y + projection * wallVector.y
    };
  }
  
  /**
   * Get nearest point on wall
   */
  private static getNearestPointOnWall(point: Point, wall: Wall): Point | null {
    const wallVector = {
      x: wall.end.x - wall.start.x,
      y: wall.end.y - wall.start.y
    };
    
    const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
    if (wallLength === 0) return null;
    
    const pointVector = {
      x: point.x - wall.start.x,
      y: point.y - wall.start.y
    };
    
    const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength * wallLength);
    const clampedProjection = Math.max(0, Math.min(1, projection));
    
    return {
      x: wall.start.x + clampedProjection * wallVector.x,
      y: wall.start.y + clampedProjection * wallVector.y
    };
  }
  
  /**
   * Get intersection point between two walls
   */
  private static getWallIntersection(wall1: Wall, wall2: Wall): Point | null {
    const x1 = wall1.start.x, y1 = wall1.start.y;
    const x2 = wall1.end.x, y2 = wall1.end.y;
    const x3 = wall2.start.x, y3 = wall2.start.y;
    const x4 = wall2.end.x, y4 = wall2.end.y;
    
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null; // Lines are parallel
    
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    
    // Check if intersection is on both line segments
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        x: x1 + t * (x2 - x1),
        y: y1 + t * (y2 - y1)
      };
    }
    
    return null;
  }
  
  /**
   * Draw snap feedback (green markers like AutoCAD)
   */
  static drawSnapFeedback(ctx: CanvasRenderingContext2D, snapResult: SimpleSnapResult) {
    if (snapResult.type === 'none') return;
    
    const { point, type } = snapResult;
    
    ctx.save();
    ctx.strokeStyle = '#00FF00'; // AutoCAD green
    ctx.fillStyle = '#00FF00';
    ctx.lineWidth = 2;
    
    // Draw different markers for different snap types
    switch (type) {
      case 'endpoint':
        // Square marker for endpoints
        ctx.strokeRect(point.x - 4, point.y - 4, 8, 8);
        break;
      case 'midpoint':
        // Triangle marker for midpoints
        ctx.beginPath();
        ctx.moveTo(point.x, point.y - 5);
        ctx.lineTo(point.x - 4, point.y + 3);
        ctx.lineTo(point.x + 4, point.y + 3);
        ctx.closePath();
        ctx.stroke();
        break;
      case 'intersection':
        // Enhanced X marker for intersections
        // If it's a line intersection snap (priority 1.5), make it more prominent
        const isLineIntersection = snapResult.priority === LINE_INTERSECTION_PRIORITY;
        const markerSize = isLineIntersection ? 6 : 4;
        
        ctx.beginPath();
        ctx.moveTo(point.x - markerSize, point.y - markerSize);
        ctx.lineTo(point.x + markerSize, point.y + markerSize);
        ctx.moveTo(point.x + markerSize, point.y - markerSize);
        ctx.lineTo(point.x - markerSize, point.y + markerSize);
        ctx.stroke();
        
        // Add extra circle for line intersection snaps
        if (isLineIntersection) {
          ctx.strokeStyle = '#FF6B00'; // Orange color for line intersections
          ctx.beginPath();
          ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
          ctx.stroke();
          ctx.strokeStyle = '#00FF00'; // Reset to green
        }
        break;
      case 'perpendicular':
        // Right angle marker for perpendicular
        ctx.beginPath();
        ctx.moveTo(point.x - 3, point.y - 3);
        ctx.lineTo(point.x - 3, point.y + 3);
        ctx.lineTo(point.x + 3, point.y + 3);
        ctx.stroke();
        break;
      default:
        // Circle for other types
        ctx.beginPath();
        ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
        ctx.stroke();
        break;
    }
    
    // Draw snap type label
    ctx.font = '10px Arial';
    const isLineIntersection = type === 'intersection' && snapResult.priority === LINE_INTERSECTION_PRIORITY;
    const labelText = isLineIntersection ? 'LINE INT' : type.toUpperCase();
    ctx.fillText(labelText, point.x + 8, point.y - 8);
    
    ctx.restore();
  }

  /**
   * Find line intersection snap - where the current wall being drawn would intersect with existing walls
   * Enhanced with scale awareness
   */
  static findLineIntersectionSnap(
    wallStart: Point,
    mousePoint: Point,
    walls: WallWithTempId[],
    scale: number = 1,
    maxDistance: number = 150, // Reduced to be less aggressive
    allowNearStartSnaps: boolean = false // Allow snaps close to start point (for resize)
  ): SimpleSnapResult {
    if (!wallStart) {
      return {
        point: mousePoint,
        type: 'none',
        priority: 999
      };
    }

    // Check if the wall being drawn is more vertical than horizontal
    const wallDirection = {
      x: mousePoint.x - wallStart.x,
      y: mousePoint.y - wallStart.y
    };
    
    const isVertical = Math.abs(wallDirection.y) > Math.abs(wallDirection.x);
    
    if (!isVertical) {
      // Skipping horizontal wall - debug logs removed
      return {
        point: mousePoint,
        type: 'none',
        priority: 999
      };
    }

    const intersectionCandidates: SimpleSnapResult[] = [];
    
    // Line intersection debug - searching for vertical wall intersections
    
    // Calculate where the current wall (from wallStart through mousePoint) would intersect existing walls
    for (let i = 0; i < walls.length; i++) {
      const wall = walls[i];
      const intersection = this.getLineToLineIntersection(wallStart, mousePoint, wall);
      
      // Wall intersection check - debug logs removed
      
      if (intersection) {
        const distanceToMouse = distance(mousePoint, intersection);
        const distanceFromStart = distance(wallStart, intersection);
        
        // Intersection found - debug logs removed
        
        // Balanced criteria for selective snapping:
        // 1. Close to the mouse cursor (not too far)
        // 2. In the direction the user is drawing
        // 3. Reasonable distance from start point (with exceptions for very close intersections)
        const isVeryCloseToMouse = distanceToMouse <= 20; // Very close intersections should always be allowed
        const isReasonableDistance = distanceFromStart > 20 && distanceFromStart <= 800;
        // For room resize, allow snaps near start point (corner already at intersection)
        const isValidDistance = allowNearStartSnaps || isVeryCloseToMouse || isReasonableDistance;
        
        if (distanceToMouse <= maxDistance && isValidDistance) {
          
          // More lenient direction checking
          const mouseVector = { x: mousePoint.x - wallStart.x, y: mousePoint.y - wallStart.y };
          const intersectionVector = { x: intersection.x - wallStart.x, y: intersection.y - wallStart.y };
          
          // Calculate dot product to check if vectors point in same direction
          const dotProduct = mouseVector.x * intersectionVector.x + mouseVector.y * intersectionVector.y;
          const mouseLength = Math.sqrt(mouseVector.x * mouseVector.x + mouseVector.y * mouseVector.y);
          const intersectionLength = Math.sqrt(intersectionVector.x * intersectionVector.x + intersectionVector.y * intersectionVector.y);
          
          // Normalize dot product to get cosine of angle between vectors
          const cosAngle = mouseLength > 0 && intersectionLength > 0 ? dotProduct / (mouseLength * intersectionLength) : 0;
          
          console.log(`🔍 Direction check:`, {
            mouseVector,
            intersectionVector,
            dotProduct,
            cosAngle,
            angleDegrees: Math.acos(Math.abs(cosAngle)) * 180 / Math.PI
          });
          
          // Only snap when clearly drawing toward the intersection
          // More restrictive to allow free drawing
          // BUT: For room resize (allowNearStartSnaps), skip direction check since corner starts at intersection
          if (allowNearStartSnaps || cosAngle > 0.3) { // Only allow up to ~70 degrees difference
            console.log(`✅ Adding intersection candidate:`, intersection);
            intersectionCandidates.push({
              point: intersection,
              type: 'intersection',
              wall,
              priority: LINE_INTERSECTION_PRIORITY
            });
          } else {
            console.log(`❌ Intersection rejected due to direction:`, { cosAngle });
          }
        } else {
          const isVeryCloseToMouse = distanceToMouse <= 20;
          const isReasonableDistance = distanceFromStart > 20 && distanceFromStart <= 800;
          const isValidDistance = isVeryCloseToMouse || isReasonableDistance;
          
          console.log(`❌ Intersection rejected due to distance:`, {
            distanceToMouse,
            maxDistance,
            distanceFromStart,
            isVeryCloseToMouse,
            isReasonableDistance,
            isValidDistance,
            reason: !isValidDistance ? 'Invalid distance' : 'Outside max distance'
          });
        }
      }
    }
    
    // Debug logging disabled for performance
    // console.log('🔍 LINE_INTERSECTION_RESULT:', {
    //   candidatesFound: intersectionCandidates.length,
    //   candidates: intersectionCandidates
    // });
    
    if (intersectionCandidates.length === 0) {
      return {
        point: mousePoint,
        type: 'none',
        priority: 999
      };
    }
    
    // Sort by distance to mouse point
    intersectionCandidates.sort((a, b) => {
      return distance(mousePoint, a.point) - distance(mousePoint, b.point);
    });
    
    return intersectionCandidates[0];
  }

  /**
   * Enhanced snap point finder that includes line intersection snapping
   */
  static findSnapPointWithLineIntersection(
    mousePoint: Point, 
    walls: WallWithTempId[], 
    scale: number = 1,
    wallStart?: Point,
    allowNearStartSnaps: boolean = false
  ): SimpleSnapResult {
    // First try enhanced regular snapping with scale and touch awareness
    const regularSnap = this.findSnapPoint(mousePoint, walls, scale, false);
    
    // If we have a wall start point, also try line intersection snapping
    if (wallStart) {
      const lineIntersectionSnap = this.findLineIntersectionSnap(wallStart, mousePoint, walls, scale, 120, allowNearStartSnaps); // More selective distance
      
      // Snap comparison - debug logs removed
      
      // Prioritize line intersection snapping when it finds a good result
      if (lineIntersectionSnap.type !== 'none') {
        // Only prefer regular endpoint snaps if they're very close (within 20px)
        if (regularSnap.type === 'endpoint' && distance(mousePoint, regularSnap.point) <= 20) {
          // Preferring close endpoint snap
          return regularSnap;
        }
        
        // Otherwise, prefer line intersection snapping
        // Using line intersection snap
        return lineIntersectionSnap;
      }
    }
    
    // Using regular snap
    return regularSnap;
  }

  /**
   * Get line-to-line intersection (not limited to segments)
   * This extends a line from wallStart through mousePoint and finds where it intersects with the existing wall
   */
  private static getLineToLineIntersection(lineStart: Point, linePoint: Point, wall: Wall): Point | null {
    // Create a line from lineStart through linePoint (extended infinitely)
    const direction = {
      x: linePoint.x - lineStart.x,
      y: linePoint.y - lineStart.y
    };
    
    // Avoid zero-length vectors
    if (Math.abs(direction.x) < 1e-10 && Math.abs(direction.y) < 1e-10) {
      console.log('🔍 Zero-length direction vector, skipping');
      return null;
    }
    
    // Create a far end point for the line (extend it far beyond the mouse point)
    const extensionFactor = 10000; // Increased extension factor for better coverage
    const lineEnd = {
      x: lineStart.x + direction.x * extensionFactor,
      y: lineStart.y + direction.y * extensionFactor
    };
    
    // Now find intersection between the extended line and the wall segment
    const x1 = lineStart.x, y1 = lineStart.y;
    const x2 = lineEnd.x, y2 = lineEnd.y;
    const x3 = wall.start.x, y3 = wall.start.y;
    const x4 = wall.end.x, y4 = wall.end.y;
    
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    
    console.log('🔍 Intersection calculation:', {
      lineStart,
      linePoint,
      lineEnd,
      wall: { start: wall.start, end: wall.end },
      denom,
      isParallel: Math.abs(denom) < 1e-6
    });
    
    if (Math.abs(denom) < 1e-6) {
      console.log('🔍 Lines are parallel, no intersection');
      return null; // Lines are parallel
    }
    
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    
    // For line intersection snapping, we want to allow intersections with infinite line extensions
    // So we don't require u to be within [0,1], but we'll apply reasonable limits
    const tValid = t >= 0; // Must be in direction of drawing
    const distanceFromWallSegment = u < 0 ? Math.abs(u) : (u > 1 ? (u - 1) : 0);
    const maxExtensionFactor = 3.0; // Allow intersections up to 3x the wall length beyond the wall
    const uValid = distanceFromWallSegment <= maxExtensionFactor;
    
    console.log('🔍 Intersection parameters:', {
      t,
      u,
      tValid,
      uValid,
      distanceFromWallSegment,
      maxExtensionFactor,
      bothValid: tValid && uValid
    });
    
    // Allow intersections with infinite line extensions (not just wall segments)
    if (tValid && uValid) {
      const intersection = {
        x: x1 + t * (x2 - x1),
        y: y1 + t * (y2 - y1)
      };
      console.log('✅ Valid intersection found (including line extensions):', intersection);
      return intersection;
    }
    
    console.log('❌ No valid intersection (parameters out of range)');
    return null;
  }
}

// Enhanced helper function that includes line intersection snapping with scale and touch awareness
export function snapToWallsWithLineIntersection(
  mousePoint: Point, 
  walls: WallWithTempId[], 
  wallStart?: Point,
  scale: number = 1,
  isTouch: boolean = false,
  allowNearStartSnaps: boolean = false
): SimpleSnapResult {
  return SimpleWallSnapping.findSnapPointWithLineIntersection(mousePoint, walls, scale, wallStart, allowNearStartSnaps);
}

// Enhanced helper function with scale and touch awareness
export function snapToWalls(mousePoint: Point, walls: WallWithTempId[], scale: number = 1, isTouch: boolean = false): SimpleSnapResult {
  return SimpleWallSnapping.findSnapPoint(mousePoint, walls, scale, isTouch);
} 